/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal, flushSync } from 'react-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import UseAxios from '../../Api/http/http';
import {
  createTbhEmployeeManagement,
  deleteTbhEmployeeManagement,
  tbhEmployeeManagementFilters,
  updateTbhEmployeeManagement,
} from '../../Api/request/tbhEmployeeManagement';
import { getRoleExperienceMappings } from '../../Api/request/projectDetails';
import { serviceName } from '../../Api/resources/serviceConstants';
import { TBH_EMPLOYEE_MANAGEMENT } from '../../Api/resources/endPoint';
import { appendServiceNameToURL, appendServicePortToURL } from '../../Api/resources/serviceURLs';
import normalizeTableUtilityResponse from '../../Utils/tableUtilityResponse';
import { getExportFileName, getExportFileNameWithDate } from '../Delivery/utils';
import { useAuth } from '../../Hooks/useAuth';

import editIcon from '../../Assets/images/svg/dar_edit.svg';
import deleteIcon from '../../Assets/images/svg/dar_delete.svg';
import './TBHEmployeeManagement.scss';

const ACTIONS = {
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
};
const PAGE_SIZE = 10;
const INITIAL_PAGINATION_CONFIG = {
  page: 1,
  limit: PAGE_SIZE,
  total: 0,
  rowsPerPageOptions: [],
  showPageSizeSelector: false,
};
const ACTIVE_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const buildServiceURL = (service, path) => {
  const servicePath = appendServiceNameToURL(service);

  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${servicePath}${path}`;
  }

  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');

  return `${baseUrl}:${port}${servicePath}${path}`;
};

const tbhEmployeeBaseURL = buildServiceURL(
  serviceName.LANDING_MS,
  `/api${TBH_EMPLOYEE_MANAGEMENT}`,
);

const EMPTY_TBH_FORM = {
  id: '',
  tbhId: '',
  employeeRegion: '',
  employeeCountry: '',
  regionCountryMappingId: '',
  employeeVerticalId: '',
  employeeContractTypeCode: '',
  employeeRoleId: '',
  employeeExperienceId: '',
  locationId: '',
  employeeDepartmentId: '',
  isActive: 'Active',
};

const TBH_FORM_FIELDS = [
  {
    name: 'tbhId',
    label: 'TBH Id',
    placeholder: 'TBH1234',
    type: 'input',
    disabled: true,
  },
  {
    name: 'employeeRegion',
    label: 'Employee Region',
    optionsKey: 'employeeRegions',
    required: true,
  },
  {
    name: 'employeeCountry',
    label: 'Employee Country',
    optionsKey: 'employeeCountries',
    required: true,
  },
  {
    name: 'employeeVerticalId',
    label: 'Employee Vertical',
    optionsKey: 'employeeVerticals',
    required: true,
  },
  {
    name: 'employeeContractTypeCode',
    label: 'Employee Contract Type',
    optionsKey: 'contractTypes',
    required: true,
  },
  {
    name: 'employeeRoleId',
    label: 'Employee Role',
    optionsKey: 'employeeRoles',
    required: true,
  },
  {
    name: 'employeeExperienceId',
    label: 'Employee Experience Category',
    optionsKey: 'employeeExperienceOptions',
    required: true,
  },
  {
    name: 'locationId',
    label: 'Location',
    optionsKey: 'locationOptions',
    required: true,
  },
  {
    name: 'employeeDepartmentId',
    label: 'Employee Department',
    optionsKey: 'departments',
    required: true,
  },
  {
    name: 'isActive',
    label: 'TBH Status',
    optionsKey: 'activeStatuses',
    required: true,
  },
];

const tbhEmployeeTableConfig = [
  {
    field: 'tbhId',
    header: 'TBH Id',
    visible: true,
    filter: true,
    sortable: true,
    order: 1,
    width: 100,
    type: 'string',
    defaultPinned: true,
    className: 'tbh-employee-id-column',
    headerClassName: 'tbh-employee-id-column',
    bodyClassName: 'tbh-employee-id-column',
  },
  {
    field: 'employeeRegion',
    header: 'Employee Region',
    visible: true,
    filter: true,
    sortable: true,
    order: 2,
    width: 135,
    type: 'multiSelect',
  },
  {
    field: 'employeeCountry',
    header: 'Employee Country',
    visible: true,
    filter: true,
    sortable: true,
    order: 3,
    width: 140,
    type: 'multiSelect',
  },
  {
    field: 'employeeVertical',
    header: 'Employee Vertical',
    visible: true,
    filter: true,
    sortable: true,
    order: 4,
    width: 150,
    type: 'multiSelect',
  },
  {
    field: 'employeeContractType',
    header: 'Employee Contract Type',
    visible: true,
    filter: true,
    sortable: true,
    order: 5,
    width: 190,
    type: 'multiSelect',
  },
  {
    field: 'employeeRole',
    header: 'Employee Role',
    visible: true,
    filter: true,
    sortable: true,
    order: 6,
    width: 130,
    type: 'multiSelect',
  },
  {
    field: 'employeeExperience',
    header: 'Employee Experience Category',
    visible: true,
    filter: true,
    sortable: true,
    order: 7,
    width: 160,
    type: 'multiSelect',
  },
  {
    field: 'location',
    header: 'Location',
    visible: true,
    filter: true,
    sortable: true,
    order: 8,
    width: 120,
    type: 'multiSelect',
  },
  {
    field: 'employeeDepartment',
    header: 'Employee Department',
    visible: true,
    filter: true,
    sortable: true,
    order: 9,
    width: 170,
    type: 'multiSelect',
  },
  {
    field: 'activeStatus',
    header: 'Active status',
    visible: true,
    filter: true,
    sortable: true,
    order: 10,
    width: 130,
    type: 'multiSelect',
  },
];

const tbhEmployeeActionColumnConfig = {
  field: 'action',
  header: 'Action',
  visible: true,
  filter: false,
  sortable: false,
  order: 11,
  width: 82,
  minWidth: '82px',
  maxWidth: '82px',
  exportable: false,
  className: 'tbh-employee-action-column',
  headerClassName: 'tbh-employee-action-column',
  bodyClassName: 'tbh-employee-action-column',
  menuItems: [],
};

const applyFrozenTbhIdColumn = (config = []) => config.map((column) => ({
  ...column,
  defaultPinned: column.field === 'tbhId',
  frozen: column.field === 'tbhId',
  ...(column.field === 'tbhId'
    ? {
      className: 'tbh-employee-id-column',
      headerClassName: 'tbh-employee-id-column',
      bodyClassName: 'tbh-employee-id-column',
    }
    : {}),
}));

const mergeTbhTableConfiguration = (responseConfig = []) => {
  const normalizedConfig = Array.isArray(responseConfig) ? responseConfig : [];
  const responseColumns = normalizedConfig.filter((column) => column.field && column.field !== 'action');
  const responseColumnMap = new Map(responseColumns.map((column) => [column.field, column]));
  const baseColumns = applyFrozenTbhIdColumn(tbhEmployeeTableConfig);
  const mergedBaseColumns = baseColumns.map((baseColumn) => ({
    ...baseColumn,
    ...(responseColumnMap.get(baseColumn.field) || {}),
  }));
  const responseOnlyColumns = responseColumns.filter(
    (column) => !baseColumns.some((baseColumn) => baseColumn.field === column.field),
  );

  return [...mergedBaseColumns, ...responseOnlyColumns];
};

const hasFilterValue = (filter = {}) => {
  if (Array.isArray(filter)) return filter.some(hasFilterValue);
  if (filter?.type && filter.type !== 'filter') return false;

  return Boolean(
    (Array.isArray(filter.selectedValues) && filter.selectedValues.length > 0)
    || (Array.isArray(filter.values) && filter.values.length > 0)
    || filter.selectedValue
    || filter.value
    || filter.filterValue
    || filter.search
    || filter.keyword
    || (Array.isArray(filter.range) && filter.range.some(Boolean)),
  );
};

const getFilterValues = (filters) => {
  if (Array.isArray(filters)) return filters;
  if (filters && typeof filters === 'object') return Object.values(filters);

  return [];
};

const hasAppliedTableFilters = (tableState = {}) => {
  const stateFilters = [
    ...getFilterValues(tableState.filters),
    ...getFilterValues(tableState.appliedFilters),
    ...getFilterValues(tableState.additionalFilters),
  ];

  return stateFilters.some(hasFilterValue);
};

const getToolbarButtons = () => {
  const toolbarButtonGroup = document.querySelector(
    '.tbh-employee-management-table-utility .button-group',
  );

  return toolbarButtonGroup
    ? Array.from(toolbarButtonGroup.querySelectorAll('button, .p-button'))
    : [];
};

const getTbhToolbarButton = (buttonType) => {
  const toolbarButtons = getToolbarButtons();

  if (buttonType === 'add') {
    const specificBtn = document.querySelector(
      '.tbh-employee-management-table-utility .add-button, '
      + '.tbh-employee-management-table-utility .table-utility-add-new-btn, '
      + '.tbh-employee-management-table-utility .tbh-employee-add-button',
    );
    if (specificBtn) return specificBtn;

    const lastBtn = toolbarButtons[toolbarButtons.length - 1];
    if (lastBtn && (
      lastBtn.classList.contains('add-button')
      || lastBtn.classList.contains('table-utility-add-new-btn')
      || lastBtn.classList.contains('tbh-employee-add-button')
      || lastBtn.querySelector('.pi-plus')
    )) {
      return lastBtn;
    }
    return null;
  }

  if (buttonType === 'filter') {
    return document.querySelector(
      '.tbh-employee-management-table-utility .table-utility-filter-btn, '
      + '.tbh-employee-management-table-utility .tbh-employee-filter-button',
    ) || toolbarButtons.find((buttonElement) => (
      buttonElement.querySelector('.pi-filter')
      || buttonElement.getAttribute('aria-label')?.toLowerCase()
        .includes('filter')
      || buttonElement.getAttribute('title')?.toLowerCase()
        .includes('filter')
      || buttonElement.getAttribute('data-pr-tooltip')?.toLowerCase()
        .includes('filter')
    )) || toolbarButtons[1];
  }

  return null;
};

const SearchableDropdown = ({
  error,
  id,
  name,
  onChange,
  options = [],
  placeholder = 'Select',
  value,
  disabled = false,
}) => {
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState('down');
  const [searchText, setSearchText] = useState('');
  const selectedOption = options.find((option) => String(option.value) === String(value));
  const displayValue = isOpen ? searchText : selectedOption?.label || '';
  const filteredOptions = useMemo(() => (
    options.filter((option) => (
      String(option.label || option.value)
        .toLowerCase()
        .includes(searchText.trim().toLowerCase())
    ))
  ), [options, searchText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateOpenDirection = () => {
    const rect = dropdownRef.current?.getBoundingClientRect();

    if (!rect) return;

    const maxMenuHeight = 232;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setOpenDirection(spaceBelow < maxMenuHeight && spaceAbove > spaceBelow ? 'up' : 'down');
  };

  const openDropdown = () => {
    if (disabled) return;
    updateOpenDirection();
    setIsOpen(true);
  };

  const handleSelect = (option) => {
    if (disabled) return;
    onChange({
      target: {
        name,
        value: option.value,
      },
    });
    setIsOpen(false);
    setSearchText('');
  };

  return (
    <div className="tbh-employee-searchable-dropdown" ref={dropdownRef}>
      <div
        className={[
          'tbh-employee-searchable-dropdown__control',
          !selectedOption ? 'is-placeholder' : '',
          error ? 'is-error' : '',
          isOpen ? 'is-open' : '',
          isOpen && openDirection === 'up' ? 'is-up' : '',
          disabled ? 'is-disabled' : '',
        ].filter(Boolean).join(' ')}
      >
        <input
          id={id}
          ref={inputRef}
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={displayValue}
          disabled={disabled}
          onFocus={openDropdown}
          onClick={openDropdown}
          onChange={(event) => {
            if (disabled) return;
            setSearchText(event.target.value);
            openDropdown();
          }}
        />
        <button
          type="button"
          disabled={disabled}
          aria-label={isOpen ? 'Close options' : 'Open options'}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (disabled) return;
            if (isOpen) {
              setIsOpen(false);
              setSearchText('');
              return;
            }

            openDropdown();
            inputRef.current?.focus();
          }}
        >
          <i className={`pi ${isOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
        </button>
      </div>
      {isOpen && !disabled && (
        <div className={`tbh-employee-searchable-dropdown__menu is-${openDirection}`}>
          <div className="tbh-employee-searchable-dropdown__options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={`${name}-${option.value}`}
                  className={[
                    'tbh-employee-searchable-dropdown__option',
                    String(option.value) === String(value) ? 'is-selected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className="tbh-employee-searchable-dropdown__empty">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

SearchableDropdown.propTypes = {
  error: PropTypes.bool,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
};

SearchableDropdown.defaultProps = {
  error: false,
  options: [],
  placeholder: 'Select',
  value: '',
  disabled: false,
};

const TbhEmployeeFormModal = ({
  dropdownOptions,
  formData,
  fieldErrors,
  mode,
  onChange,
  onClose,
  onSubmit,
}) => createPortal(
  <div className="tbh-employee-modal-backdrop">
    <form className="tbh-employee-modal" onSubmit={onSubmit}>
      <div className="tbh-employee-modal__header">
        {mode === ACTIONS.EDIT ? 'Edit TBH Employee' : 'Add TBH Employee'}
      </div>
      <div className="tbh-employee-modal__body">
        {TBH_FORM_FIELDS.map((field) => (
          <div className="tbh-employee-modal__field" key={field.name}>
            <label
              htmlFor={`tbh-employee-${field.name}`}
              className={fieldErrors[field.name] ? 'is-error' : ''}
            >
              {field.label}
              {field.required && <span className="required-asterisk">*</span>}
            </label>
            {field.type === 'input' ? (
              <input
                id={`tbh-employee-${field.name}`}
                name={field.name}
                type="text"
                placeholder={field.placeholder}
                value={formData[field.name]}
                disabled={field.disabled}
                onChange={onChange}
              />
            ) : (
              <SearchableDropdown
                id={`tbh-employee-${field.name}`}
                name={field.name}
                value={formData[field.name]}
                options={dropdownOptions[field.optionsKey] || []}
                error={fieldErrors[field.name]}
                disabled={
                  (field.name === 'employeeCountry' && !formData.employeeRegion)
                  || (field.name === 'employeeExperienceId' && !formData.employeeRoleId)
                }
                onChange={onChange}
              />
            )}
          </div>
        ))}
      </div>
      <div className="tbh-employee-modal__footer">
        <button
          type="button"
          className="tbh-employee-modal__btn tbh-employee-modal__btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="tbh-employee-modal__btn tbh-employee-modal__btn--primary"
        >
          Save
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const TbhEmployeeDeleteModal = ({ row, onClose, onConfirm }) => createPortal(
  <div className="tbh-employee-modal-backdrop">
    <div className="tbh-employee-delete-modal" role="dialog" aria-modal="true">
      <div className="tbh-employee-delete-modal__header">Delete TBH Employee</div>
      <div className="tbh-employee-delete-modal__body">
        Are you sure you want to delete {row?.tbhId || 'this TBH employee'}?
      </div>
      <div className="tbh-employee-modal__footer">
        <button
          type="button"
          className="tbh-employee-modal__btn tbh-employee-modal__btn--ghost"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="tbh-employee-modal__btn tbh-employee-modal__btn--danger"
          onClick={onConfirm}
        >
          Delete
        </button>
      </div>
    </div>
  </div>,
  document.body,
);

const buildTbhEmployeePayload = (formData) => ({
  regionCountryMappingId: Number(formData.regionCountryMappingId),
  region_country_mapping_id: Number(formData.regionCountryMappingId),
  employeeVerticalId: Number(formData.employeeVerticalId),
  employee_vertical_id: Number(formData.employeeVerticalId),
  employeeContractTypeCode: formData.employeeContractTypeCode,
  employee_contract_type: formData.employeeContractTypeCode,
  employeeRoleId: Number(formData.employeeRoleId),
  employee_role_id: Number(formData.employeeRoleId),
  employeeExperienceId: formData.employeeExperienceId,
  employee_experience_id: formData.employeeExperienceId,
  locationId: Number(formData.locationId),
  location_id: Number(formData.locationId),
  employeeDepartmentId: Number(formData.employeeDepartmentId),
  employee_department_id: Number(formData.employeeDepartmentId),
  isActive: normalizeActiveValue(formData.isActive),
  is_active: normalizeActiveValue(formData.isActive),
});

const normalizeText = (value = '') => String(value).trim();

const getTbhApiErrorMessage = (response, fallbackMessage) => (
  response?.data?.response?.data?.message
  || response?.data?.response?.data?.error
  || response?.data?.message
  || response?.data?.error
  || response?.message
  || fallbackMessage
);

const isSuccessfulApiResponse = (response) => response?.status >= 200 && response.status < 300;

const normalizeActiveValue = (value, fallback = true) => {
  if (value === true || value === false) return value;
  if (value === undefined || value === null || value === '') return fallback;

  const normalizedValue = String(value)
    .trim()
    .toLowerCase();

  if (['active', 'true', '1', 'yes', 'y'].includes(normalizedValue)) return true;
  if (['inactive', 'false', '0', 'no', 'n'].includes(normalizedValue)) return false;

  return fallback;
};

const normalizeExperienceLabel = (value) => normalizeText(value)
  .replace(/\s*(yrs?|years?)$/i, '')
  .replace(/\s*-\s*/g, '-')
  .replace(/\s*\+\s*$/g, '+')
  .replace(/\s+/g, ' ');

const getExperienceSortParts = (value) => {
  const label = normalizeExperienceLabel(value).toLowerCase();
  const compactLabel = label.replace(/[\s_-]/g, '');

  if (compactLabel === 'junior') return { group: 1, start: 0, end: 0 };
  if (compactLabel === 'midlevel') return { group: 1, start: 1, end: 1 };
  if (compactLabel === 'senior') return { group: 1, start: 2, end: 2 };

  const rangeMatch = label.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return { group: 0, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
  }

  const plusMatch = label.match(/^(\d+(?:\.\d+)?)\+$/);
  if (plusMatch) {
    return { group: 0, start: Number(plusMatch[1]), end: Number(plusMatch[1]) };
  }

  const numericMatch = label.match(/^(\d+(?:\.\d+)?)$/);
  if (numericMatch) {
    return { group: 2, start: Number(numericMatch[1]), end: Number(numericMatch[1]) };
  }

  return { group: 3, start: 0, end: 0 };
};

const sortExperienceOptions = (options = [], getLabel = (option) => option?.label ?? option) => (
  [...options].sort((first, second) => {
    const firstLabel = normalizeExperienceLabel(getLabel(first));
    const secondLabel = normalizeExperienceLabel(getLabel(second));
    const firstSort = getExperienceSortParts(firstLabel);
    const secondSort = getExperienceSortParts(secondLabel);

    return firstSort.group - secondSort.group
      || firstSort.start - secondSort.start
      || firstSort.end - secondSort.end
      || firstLabel.localeCompare(secondLabel, undefined, { numeric: true, sensitivity: 'base' });
  })
);

const dedupeOptionsByValue = (options = []) => {
  const optionMap = new Map();

  options.forEach((option) => {
    const value = normalizeText(option.value);

    if (!value || optionMap.has(value)) return;

    optionMap.set(value, {
      ...option,
      value,
      label: normalizeText(option.label || value),
    });
  });

  return Array.from(optionMap.values());
};

const buildRoleExperienceLookup = (roleExperienceMappings = []) => {
  const lookup = new Map();

  roleExperienceMappings.forEach((mapping, index) => {
    const roleId = normalizeText(mapping.roleId ?? mapping.role_id);
    const experienceId = normalizeText(
      mapping.experienceId
        ?? mapping.experience_id
        ?? mapping.experienceCategoryId
        ?? mapping.experience_category_id,
    );
    const experienceLabel = normalizeText(
      mapping.experienceRange
        ?? mapping.experience_range
        ?? mapping.experience
        ?? mapping.label,
    ).toLowerCase();

    if (!roleId) return;

    if (!lookup.has(roleId)) {
      lookup.set(roleId, { ids: new Map(), labels: new Map() });
    }

    if (experienceId) lookup.get(roleId).ids.set(experienceId, index);
    if (experienceLabel) lookup.get(roleId).labels.set(experienceLabel, index);
  });

  return lookup;
};

const buildModalDropdownOptions = (dropdownOptions, formData, roleExperienceMappings = []) => {
  const employeeCountries = formData.employeeRegion
    ? dedupeOptionsByValue(
      (dropdownOptions.regionCountries || [])
        .filter((item) => normalizeText(item.regionName) === normalizeText(formData.employeeRegion))
        .map((item) => ({
          value: item.countryName,
          label: item.countryName,
          regionName: item.regionName,
        })),
    )
    : dedupeOptionsByValue(dropdownOptions.employeeCountries || []);

  const roleExperienceLookup = buildRoleExperienceLookup(roleExperienceMappings);
  const allowedExperiences = roleExperienceLookup.get(normalizeText(formData.employeeRoleId));
  const allEmployeeExperienceOptions = dropdownOptions.employeeExperienceOptions
    || dropdownOptions.experienceCategories
    || [];
  const employeeExperienceOptions = formData.employeeRoleId && allowedExperiences
    ? sortExperienceOptions(
      allEmployeeExperienceOptions.filter((option) => (
        allowedExperiences.ids.has(normalizeText(option.value))
        || allowedExperiences.labels.has(normalizeText(option.label).toLowerCase())
      )),
    )
    : [];

  return {
    ...dropdownOptions,
    activeStatuses: dropdownOptions.activeStatuses || ACTIVE_STATUS_OPTIONS,
    employeeCountries,
    employeeExperienceOptions,
  };
};

const findRegionCountryMappingId = ({ employeeCountry, employeeRegion }, regionCountries = []) => {
  const match = regionCountries.find((item) => (
    normalizeText(item.regionName) === normalizeText(employeeRegion)
    && normalizeText(item.countryName) === normalizeText(employeeCountry)
  ));

  return match?.value || '';
};

const updateRoleAndResetExp = (setForm, roleId) => {
  flushSync(() => {
    setForm((prev) => ({
      ...prev,
      employeeRoleId: roleId ? String(roleId) : '',
      employeeExperienceId: '',
    }));
  });
};

const buildTbhEmployeeTableData = (rows, handleRowAction, canEdit, canDelete) => rows.map((row) => ({
  ...row,
  activeStatus: row.activeStatus || (normalizeActiveValue(row.is_active ?? row.isActive) ? 'Active' : 'Inactive'),
  action: (
    <div className="tbh-employee-action-cell" onClick={(event) => event.stopPropagation()}>
      {canEdit && (
        <button
          type="button"
          className="tbh-employee-action-btn"
          aria-label={`Edit ${row.tbhId}`}
          title="Edit"
          onClick={() => handleRowAction(ACTIONS.EDIT, row)}
        >
          <img src={editIcon} alt="" aria-hidden="true" className="tbh-employee-action-icon" />
        </button>
      )}

      {canDelete && (
        <button
          type="button"
          className="tbh-employee-action-btn"
          aria-label={`Delete ${row.tbhId}`}
          title="Delete"
          onClick={() => handleRowAction(ACTIONS.DELETE, row)}
        >
          <img src={deleteIcon} alt="" aria-hidden="true" className="tbh-employee-action-icon" />
        </button>
      )}
    </div>
  ),
}));

const buildTbhEmployeeConfiguration = ({
  canAdd,
  handleAddClick,
  handleFilteredApiResponse,
  handleTableState,
  tableConfig,
  userProfile,
}) => ({
  generalSettings: {
    enableApiFiltering: true,
    exportHeaderUnmerge: true,
    baseURL: tbhEmployeeBaseURL,
    headers: { token: userProfile?.access_token || '' },
    userInfo: {
      id: userProfile?.id || '',
      emailId: userProfile?.email || '',
      permissions: userProfile?.permissions || [],
      roleDetails: userProfile?.roleDetails || [],
      employeeId: userProfile?.employeeId || '',
    },
    showToolBar: true,
    showPagination: true,
    handler: {
      handleFilteredApiResponse,
      setTableStateHandler: handleTableState,
    },
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: true },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: {
        endpoint: 'export',
        enabled: true,
        filename: () => getExportFileName('TBH Employee Management'),
        sheetName: () => getExportFileNameWithDate('TBH Employee Management'),
      },
    },
    toolBarSettings: {
      showToolBarHeading: true,
      toolBarHeading: 'TBH Employee Management',
      showAddNew: canAdd,
      showDownloadExcel: true,
      showColumnCustomize: false,
      showTieredMenu: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showFilterOption: true,
      showReset: false,
      showExpandCollapse: false,
    },
    handlers: {
      handleAddIconClick: handleAddClick,
    },
  },
  showToolBar: true,
  tableSettings: {
    size: 'small',
    pageSize: PAGE_SIZE,
    tableConfig,
    tableScrollHeight: 'max-content',
    paginationConfig: INITIAL_PAGINATION_CONFIG,
    loaderSkeleton: {
      rows: PAGE_SIZE,
      bodyCells: 1,
      columns: tableConfig.length,
    },
    enableTreeTable: false,
    maxFrozenColumns: {
      desktop: 1,
      tablet: 1,
      mobile: 1,
    },
  },
});

const TBHEmployeeManagement = () => {
  const { userProfile } = useAuth();
  const isSuperAdmin = useMemo(() => {
    return userProfile?.roleDetails?.some((role) =>
      role.roleName?.toLowerCase().includes('super admin'),
    );
  }, [userProfile]);

  const tbhPermissions = userProfile?.permissions?.['resource:tbh-employee-management']
    || userProfile?.permissions?.['resource:tbh_employee_management']
    || [];
  const canAdd = isSuperAdmin
    || tbhPermissions.includes('add')
    || tbhPermissions.includes('create')
    || tbhPermissions.includes('write');
  const canEdit = isSuperAdmin
    || tbhPermissions.includes('update')
    || tbhPermissions.includes('edit')
    || tbhPermissions.includes('write');
  const canDelete = false;
  const canShowActionColumn = canEdit || canDelete;
  const tableStateRef = useRef(null);

  const [tableConfig, setTableConfig] = useState(() => {
    return [
      ...applyFrozenTbhIdColumn(tbhEmployeeTableConfig),
      ...(canShowActionColumn ? [tbhEmployeeActionColumnConfig] : []),
    ];
  });

  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [roleExperienceMappings, setRoleExperienceMappings] = useState([]);
  const dropdownOptionsRef = useRef({}); // always up-to-date, no stale-closure risk
  const [formModal, setFormModal] = useState({
    isOpen: false,
    mode: ACTIONS.ADD,
    selectedRowId: null,
  });
  const [deleteRow, setDeleteRow] = useState(null);
  const [formData, setFormData] = useState(EMPTY_TBH_FORM);
  const [fieldErrors, setFieldErrors] = useState({});

  const refreshTableData = useCallback(() => {
    const tableState = tableStateRef.current?.();

    if (tableState?.fetchTableData) {
      tableState.fetchTableData(
        tableState.filters,
        tableState.pagination,
        tableState.searchQuery,
      );
    }
  }, []);

  const loadDropdownOptions = useCallback(async () => {
    const [response, mappingsResponse] = await Promise.all([
      UseAxios(tbhEmployeeManagementFilters()),
      UseAxios(getRoleExperienceMappings()).catch(() => ({ data: { roleExperienceMappings: [] } })),
    ]);
    const nextDropdownOptions = response?.data?.data || {};
    const nextRoleExperienceMappings = mappingsResponse?.data?.roleExperienceMappings
      || mappingsResponse?.data?.data?.roleExperienceMappings
      || mappingsResponse?.data?.data
      || [];

    setDropdownOptions(nextDropdownOptions);
    setRoleExperienceMappings(
      Array.isArray(nextRoleExperienceMappings) ? nextRoleExperienceMappings : [],
    );
    dropdownOptionsRef.current = nextDropdownOptions; // keep ref in sync

    return nextDropdownOptions;
  }, []);

  useEffect(() => {
    loadDropdownOptions();
  }, [loadDropdownOptions]);

  useEffect(() => {
    const updateAddButtonTooltip = () => {
      const addButton = getTbhToolbarButton('add');
      if (!addButton) return;
      addButton.classList.add('tbh-employee-add-button');

      if (!canAdd) {
        addButton.setAttribute('disabled', 'true');
        addButton.style.opacity = '0.45';
        addButton.style.cursor = 'not-allowed';
        addButton.style.pointerEvents = 'none'; // Prevents hovering
      } else {
        addButton.removeAttribute('disabled');
        addButton.style.opacity = '1';
        addButton.style.cursor = 'pointer';
        addButton.style.pointerEvents = 'auto';
      }

      addButton.removeAttribute('title');
      addButton.setAttribute('aria-label', 'Add TBH Employee');
      addButton.setAttribute('data-pr-tooltip', 'Add TBH Employee');
    };

    const updateFilterButtonState = () => {
      const filterButton = getTbhToolbarButton('filter');

      if (!filterButton) return;

      filterButton.classList.add('tbh-employee-filter-button');
      filterButton.classList.toggle(
        'tbh-employee-filter-active',
        hasAppliedTableFilters(tableStateRef.current?.()),
      );
    };
    const syncToolbarButtons = () => {
      updateAddButtonTooltip();
      updateFilterButtonState();
    };
    const syncToolbarButtonsAfterStateChange = () => requestAnimationFrame(syncToolbarButtons);

    syncToolbarButtons();

    const tableContainer = document.querySelector('.tbh-employee-management-table-utility');
    const buttonObserver = new MutationObserver(syncToolbarButtons);

    if (tableContainer) {
      buttonObserver.observe(tableContainer, { childList: true, subtree: true });
      tableContainer.addEventListener('click', syncToolbarButtons, true);
      tableContainer.addEventListener('click', syncToolbarButtonsAfterStateChange, true);
    }

    const tooltipObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1 || !node.classList?.contains('p-tooltip')) return;

          const textElement = node.querySelector('.p-tooltip-text');

          if (textElement?.textContent.trim() === 'Add New') {
            textElement.textContent = 'Add TBH Employee';
          }
        });
      });
    });

    tooltipObserver.observe(document.body, { childList: true });

    return () => {
      buttonObserver.disconnect();
      tooltipObserver.disconnect();
      tableContainer?.removeEventListener('click', syncToolbarButtons, true);
      tableContainer?.removeEventListener('click', syncToolbarButtonsAfterStateChange, true);
    };
  }, [canAdd]);

  const handleCloseFormModal = useCallback(() => {
    setFormModal({ isOpen: false, mode: ACTIONS.ADD, selectedRowId: null });
    setFormData(EMPTY_TBH_FORM);
    setFieldErrors({});
  }, []);

  const openAddModal = useCallback(async () => {
    const nextDropdownOptions = Object.keys(dropdownOptions).length > 0
      ? dropdownOptions
      : await loadDropdownOptions();

    setFormData({
      ...EMPTY_TBH_FORM,
      tbhId: nextDropdownOptions.nextTbhId || '',
    });
    setFieldErrors({});
    setFormModal({ isOpen: true, mode: ACTIONS.ADD, selectedRowId: null });
  }, [dropdownOptions, loadDropdownOptions]);

  const handleRowAction = useCallback((action, row) => {
    if (action === ACTIONS.DELETE) {
      setDeleteRow(row);
      return;
    }

    if (action === ACTIONS.EDIT) {
      setFormData({
        ...EMPTY_TBH_FORM,
        id: row.id || '',
        tbhId: row.tbhId || '',
        employeeRegion: row.employeeRegion || '',
        employeeCountry: row.employeeCountry || '',
        regionCountryMappingId: row.regionCountryMappingId || '',
        employeeVerticalId: row.employeeVerticalId || '',
        employeeContractTypeCode: row.employeeContractTypeCode || '',
        employeeRoleId: row.employeeRoleId || '',
        employeeExperienceId: row.employeeExperienceId || '',
        locationId: row.locationId || '',
        employeeDepartmentId: row.employeeDepartmentId || '',
        isActive: normalizeActiveValue(row.is_active ?? row.isActive ?? row.activeStatus)
          ? 'Active'
          : 'Inactive',
      });
      setFieldErrors({});
      setFormModal({ isOpen: true, mode: ACTIONS.EDIT, selectedRowId: row.id });
      return;
    }

    openAddModal();
  }, [openAddModal]);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;

    if (name === 'employeeRoleId') {
      updateRoleAndResetExp(setFormData, value);
      setFieldErrors((prev) => ({
        ...prev,
        [name]: false,
        employeeExperienceId: false,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'employeeRegion'
        ? {
          employeeCountry: '',
          regionCountryMappingId: '',
        }
        : {}),
      ...(name === 'employeeCountry'
        ? {
          regionCountryMappingId: findRegionCountryMappingId(
            { ...prev, employeeCountry: value },
            dropdownOptions.regionCountries,
          ),
        }
        : {}),
    }));
    setFieldErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  }, [dropdownOptions.regionCountries]);

  const validateForm = useCallback(() => {
    const nextErrors = TBH_FORM_FIELDS.reduce((errors, field) => {
      if (field.required && !formData[field.name]) {
        return {
          ...errors,
          [field.name]: true,
        };
      }

      return errors;
    }, {});

    setFieldErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }, [formData]);

  const handleSubmitForm = useCallback((event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = buildTbhEmployeePayload(formData);
    const request = formModal.mode === ACTIONS.EDIT
      ? updateTbhEmployeeManagement(formModal.selectedRowId, payload)
      : createTbhEmployeeManagement(payload);

    UseAxios(request, setIsLoading).then((response) => {
      if (!isSuccessfulApiResponse(response)) {
        toast.error(getTbhApiErrorMessage(
          response,
          'Failed to save TBH employee management record.',
        ));
        return;
      }

      handleCloseFormModal();
      refreshTableData();
    });
  }, [
    formData,
    formModal.mode,
    formModal.selectedRowId,
    handleCloseFormModal,
    refreshTableData,
    validateForm,
  ]);

  const handleConfirmDelete = useCallback(() => {
    UseAxios(deleteTbhEmployeeManagement(deleteRow?.id), setIsLoading).then((response) => {
      if (!isSuccessfulApiResponse(response)) {
        toast.error(getTbhApiErrorMessage(
          response,
          'Failed to delete TBH employee management record.',
        ));
        return;
      }

      setDeleteRow(null);
      refreshTableData();
    });
  }, [deleteRow, refreshTableData]);

  // Normalise any option array to { label, value } pairs
  const toTbhOptions = (arr = []) => {

    if (!Array.isArray(arr)) return [];
    return arr.map((item) => ({
      label: item?.label ?? item?.name ?? item?.title ?? String(item?.value ?? item?.id ?? ''),
      value: String(item?.value ?? item?.id ?? item?.code ?? item?.label ?? ''),
      ...item,
    })).filter((opt) => opt.label && opt.value);
  };

  // Inject fetched dropdown options into tableConfig filter items
  const injectTbhFilterOptions = useCallback((tableConfigArray) => {
    const opts = dropdownOptionsRef.current;
    const optionsByField = {
      employeeRegion: opts.employeeRegions || opts.regions || [],
      employeeCountry: opts.employeeCountries || opts.countries || [],
      employeeVertical: opts.employeeVerticals || opts.verticals || [],
      employeeVerticalId: opts.employeeVerticals || opts.verticals || [],
      employeeContractType: opts.contractTypes || [],
      employeeContractTypeCode: opts.contractTypes || [],
      employeeRole: opts.employeeRoles || opts.roles || [],
      employeeRoleId: opts.employeeRoles || opts.roles || [],
      employeeExperience: opts.employeeExperienceOptions || opts.experienceCategories || [],
      employeeExperienceId: opts.employeeExperienceOptions || opts.experienceCategories || [],
      location: opts.locationOptions || opts.locations || [],
      locationId: opts.locationOptions || opts.locations || [],
      employeeDepartment: opts.employeeDepartments || opts.departments || [],
      employeeDepartmentId: opts.employeeDepartments || opts.departments || [],
      activeStatus: opts.activeStatuses || ACTIVE_STATUS_OPTIONS,
      isActive: opts.activeStatuses || ACTIVE_STATUS_OPTIONS,
    };

    return tableConfigArray.map((col) => {
      if (!Array.isArray(col.menuItems)) return col;
      return {
        ...col,
        menuItems: col.menuItems.map((menu) => ({
          ...menu,
          items: (menu.items || []).map((filterItem) => {
            if (filterItem?.type !== 'filter') return filterItem;
            const fieldKey = filterItem.field || col.field || '';
            const rawOpts = optionsByField[fieldKey];
            if (!rawOpts || rawOpts.length === 0) return filterItem;
            const labelFilterFields = [
              'employeeVertical',
              'employeeRole',
              'employeeExperience',
              'location',
              'employeeDepartment',
            ];
            const normalised = toTbhOptions(rawOpts).map((option) => ({
              ...option,
              value: labelFilterFields.includes(fieldKey) ? option.label : option.value,
            }));
            if (normalised.length === 0) return filterItem;
            return { ...filterItem, options: normalised, values: normalised };
          }),
        })),
      };
    });
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    normalizeTableUtilityResponse(response);

    const responseTableConfiguration = Array.isArray(response.data?.tableConfiguration)
      ? response.data.tableConfiguration
      : [];
    const tableData = Array.isArray(response.data?.tableData)
      ? response.data.tableData
      : [];

    const tableConfigurationWithActions = [
      ...injectTbhFilterOptions(mergeTbhTableConfiguration(responseTableConfiguration)),
      ...(canShowActionColumn
        ? [{
          ...tbhEmployeeActionColumnConfig,
          order: responseTableConfiguration.length + 1,
        }]
        : []),
    ];

    response.data.tableConfiguration = tableConfigurationWithActions;
    response.data.tableData = buildTbhEmployeeTableData(tableData, handleRowAction, canEdit, canDelete);
    response.meta = {
      ...(response.meta || {}),
      page: response.meta?.page || 1,
      limit: PAGE_SIZE,
      total: response.meta?.total || tableData.length,
    };
    response.data.meta = response.meta;

    setTableConfig(tableConfigurationWithActions);

    requestAnimationFrame(() => {
      const filterButton = getTbhToolbarButton('filter');

      filterButton?.classList.add('tbh-employee-filter-button');

      filterButton?.classList.toggle(
        'tbh-employee-filter-active',
        hasAppliedTableFilters(tableStateRef.current?.()),
      );
    });
  }, [handleRowAction, canEdit, canDelete, canShowActionColumn, injectTbhFilterOptions]);

  const handleTableState = useCallback((getState) => {
    tableStateRef.current = getState;
  }, []);


  const configuration = useMemo(
    () => buildTbhEmployeeConfiguration({
      canAdd,
      handleAddClick: () => {
        if (canAdd) handleRowAction(ACTIONS.ADD, null);
      },
      handleFilteredApiResponse,
      handleTableState,
      tableConfig,
      userProfile,
    }),
    [handleFilteredApiResponse, handleRowAction, handleTableState, tableConfig, userProfile, canAdd],
  );

  const modalDropdownOptions = useMemo(
    () => buildModalDropdownOptions(dropdownOptions, formData, roleExperienceMappings),
    [dropdownOptions, formData, roleExperienceMappings],
  );

  return (
    <section className="tbh-employee-management-outer-layer">
      <div className="tbh-employee-management-table-container">
        <div className="tbh-employee-management-table-utility">
          <TableUtility
            configuration={configuration}
            loading={isLoading}
            showFilterOption
          />
        </div>
      </div>
      {formModal.isOpen && (
        <TbhEmployeeFormModal
          dropdownOptions={modalDropdownOptions}
          fieldErrors={fieldErrors}
          formData={formData}
          mode={formModal.mode}
          onChange={handleFormChange}
          onClose={handleCloseFormModal}
          onSubmit={handleSubmitForm}
        />
      )}
      {deleteRow && (
        <TbhEmployeeDeleteModal
          row={deleteRow}
          onClose={() => setDeleteRow(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </section>
  );
};

export default TBHEmployeeManagement;
